import { Controller, UseFormReturn } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { ObraArquivoForm } from "../../../schemas/Obras_informacoesgerais/obra_arquivo";
import { normalizeFileName } from "../../../../../../common/utils/utils";
import { FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, IconButton, styled, FormHelperText } from "@mui/material";
import { ObraArquivo } from "../../../../domain/models/obra_arquivo";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import dayjs from "dayjs";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * ObraArquivoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedObraArquivo: ObraArquivo | null,
  reactHookForm: UseFormReturn<ObraArquivoForm>,
  onClose: () => void;
  onSubmit: (e: ObraArquivoForm) => void;
  onDownload: (idObraArquivo: number, arquivoNome: string) => void;
};

export const ObraArquivoModal = ({
  open,
  saving,
  loading,
  selectedObraArquivo,
  reactHookForm: hookForm,
  onClose,
  onSubmit,
  onDownload,
}: Props) => {
  const [documento, setDocumento] = useState(selectedObraArquivo);

  useEffect(() => {
    if (open) {
      if (selectedObraArquivo) {
        hookForm.setValue('statusArquivo', selectedObraArquivo.obsoleto ? 2 : 1);
        hookForm.setValue('nomeDocumento', selectedObraArquivo.descricao ?? '');

        if (selectedObraArquivo.dataVencimento) {
          hookForm.setValue('dataVencimento', dayjs(selectedObraArquivo.dataVencimento).toDate());
        } else {
          hookForm.resetField('dataVencimento');
        }

        hookForm.resetField('file');
      } else {
        hookForm.reset();
      }
      setDocumento(selectedObraArquivo ?? null);
    }
  }, [
    open,
    selectedObraArquivo,
    hookForm,
  ]);

  const handleSubmit = useCallback((form: ObraArquivoForm) => {
    if (documento === null && form.file === null) {
      hookForm.setError('file', { message: 'É necessario selecionar o documento a ser enviado' });
      return;
    }
    if (!saving) onSubmit(form);
  }, [
    documento,
    hookForm,
    saving,
    onSubmit,
  ]);

  const handleDownload = useCallback(() => {
    if (!saving && documento) {
      onDownload(documento.idObraArquivo, documento.nome ?? '');
    }
  }, [
    saving,
    documento,
    onDownload,
  ]);

  const handleDeleteFile = useCallback(() => {
    if (!saving) setDocumento(null);
  }, [
    saving,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving && e.target.files && e.target.files.length > 0) {
      onChange(e.target.files.item(0));
    }
  }, [
    saving,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title={'Documento'}
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(handleSubmit)}>
      <FormContainer title={"Informações"} arrangement="fullRow">
        <>
          <Controller
            name='statusArquivo'
            control={hookForm.control}
            render={({ field }) => (
              <FormControl size="small" className="grid-col-1" required={true}>
                <FormLabel id="statusArquivo" required={true}>
                  Status do Arquivo
                </FormLabel>
                <RadioGroup
                  aria-labelledby="statusArquivo"
                  name="statusArquivo"
                  row={true}
                  value={field.value ?? null}
                  onChange={(e) => { if (!saving) field.onChange(e); }}>

                  <FormControlLabel
                    className="text-black dark:text-white"
                    value={1}
                    control={<Radio />}
                    label="Atual" />

                  <FormControlLabel
                    className="text-black dark:text-white"
                    value={2}
                    control={<Radio />}
                    label="Obsoleto" />

                </RadioGroup>
              </FormControl>
            )} />

          <DefaultFormInput
            name={'nomeDocumento'}
            label={"Nome do Documento"}
            readOnly={saving}
            required={true}
            reactHookForm={hookForm}
          />

          <FormDatePickerInput
            name='dataVencimento'
            readOnly={saving}
            label="Data de Vencimento"
            reactHookForm={hookForm}
          />

          <div className="flex w-full">
            <Controller
              name='file'
              control={hookForm.control}
              render={({ field }) => (documento && documento.nome && documento.nome.length > 0) ? (
                <div className="w-full flex flex-row justify-between items-center">
                  <span
                    className="cursor-pointer"
                    onMouseDown={handleDownload}>
                    {documento.nome && documento.nome.length > 70
                      ? `${documento.nome.substring(0, 70)}...`
                      : documento.nome}
                  </span>
                  <IconButton onClick={handleDeleteFile}>
                    <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                  </IconButton>
                </div>
              ) : (
                <div className="w-full flex flex-col">
                  <PrimaryButton
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    disabled={saving}
                    startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                    {field.value ? normalizeFileName(field.value?.name, 40) : "Escolher arquivo"}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e) => handleFileChange(e, field.onChange)}
                      multiple={false} />
                  </PrimaryButton>

                  <FormHelperText id={`file-component-error-text`} error={hookForm.formState.errors.file !== undefined}>
                    {hookForm.formState.errors.file?.message}
                  </FormHelperText>
                </div>
              )} />
          </div>
        </>
      </FormContainer>
    </BaseFormControlledModal>
  );
}